import React, { useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import Navbar from "./components/Navbar";
import MembershipRenewalBar from "./components/MembershipRenewalBar";
import PageBackground from "./components/PageBackground";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import { ServerCacheProvider } from "./state/ServerCacheProvider";
import { PageSkeleton } from "./components/LoadingState";
import { AccountRoute } from "./components/AccountRoute";
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
// Load code alongside auth, retaining AccountRoute around the private page.
const Dashboard = lazy(() => import("./pages/Dashboard").then(({ default: DashboardPage }) => ({
  default: () => <AccountRoute><DashboardPage /></AccountRoute>,
})));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Upload = lazy(() => import("./pages/Upload"));
const RecordDetail = lazy(() => import("./pages/RecordDetail"));
const ReviewDetail = lazy(() => import("./pages/ReviewDetail"));
const InstitutionLanding = lazy(() => import("./pages/InstitutionLanding"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const MembershipTerms = lazy(() => import("./pages/MembershipTerms"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Calculator = lazy(() => import("./pages/Calculator"));
const CostCalculator = lazy(() => import("./pages/CostCalculator"));
const Signup = lazy(() => import("./pages/Signup"));
const Register = lazy(() => import("./pages/Register"));
const Verify = lazy(() => import("./pages/Verify"));
const SearchUploads = lazy(() => import("./pages/SearchUploads"));
const Resource = lazy(() => import("./pages/Resource"));
const ResourceDissertation = lazy(() => import("./pages/ResourceDissertation"));
const Support = lazy(() => import("./pages/Support"));
const DocsPage = lazy(() => import("./pages/docs/DocsPage"));
const BulkUploadJobPage = lazy(() => import("./pages/BulkUploadJobPage"));
const RenewMembership = lazy(() => import("./pages/RenewMembership"));

function DocsRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/docs/${slug}`} replace />;
}

function AppContent() {
  const location = useLocation();
  const isUpload =
    location.pathname === "/upload" ||
    location.pathname === "/uploads" ||
    location.pathname === "/uploads/new";
  const isRecordDetail = location.pathname.startsWith("/records/");
  const isInstitution = location.pathname.startsWith("/institutions/");
  const isLogin = location.pathname === "/login";
  const isRegister = location.pathname === "/register";
  const isForgotPassword = location.pathname === "/forgot-password";
  const isResetPassword = location.pathname.startsWith("/reset/");
  const isResource = location.pathname === "/resource";
  const isResourceDissertation = location.pathname === "/resource-dissertation";
  const isHomePage = location.pathname === "/";

  const hideNavFooter =
    isUpload ||
    isRecordDetail ||
    isInstitution ||
    isLogin ||
    isRegister ||
    isForgotPassword ||
    isResetPassword ||
    isResource ||
    isResourceDissertation;

  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.hash, location.pathname]);

  useEffect(() => {
    if (!location.hash) return;

    const id = decodeURIComponent(location.hash.slice(1));
    const target = document.getElementById(id);

    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.hash, location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Homepage-specific background and overlays */}
      {isHomePage && <PageBackground />}

      {/* Navbar */}
      {!hideNavFooter && <Navbar />}

      {/* Spacer so fixed navbar does not overlap content */}
      {!hideNavFooter && <div className="h-24 shrink-0" aria-hidden="true" />}

      {/* Renewal bar for the final week of a membership and after it lapses.
          Below the navbar spacer so it pushes content rather than hiding
          under the fixed nav, and outside `hideNavFooter` pages for the same
          reason those hide the nav: they are full-bleed, chromeless views. */}
      {!hideNavFooter && <MembershipRenewalBar />}

      {/* Main content area */}
      <main className="flex-grow">
        <Suspense fallback={<PageSkeleton />}><Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/cost-calculator" element={<CostCalculator />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/reviews/:requestId" element={<AccountRoute><ReviewDetail /></AccountRoute>} />
          <Route path="/dashboard/bulk/jobs/:jobId" element={<AccountRoute><BulkUploadJobPage /></AccountRoute>} />
          {/* An institution opens inside the dashboard shell, with its nav and footer. */}
          <Route path="/dashboard/institutions/:slug" element={<Dashboard />} />
          <Route path="/dashboard/renew" element={<AccountRoute><RenewMembership /></AccountRoute>} />
          <Route path="/dashboard/:tab" element={<Dashboard />} />
          <Route path="/me/uploads" element={<Navigate to="/dashboard" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset/:token" element={<ResetPassword />} />
          <Route path="/upload" element={<Upload />} />
          <Route path="/uploads" element={<Navigate to="/upload" replace />} />
          <Route path="/uploads/new" element={<Navigate to="/upload" replace />} />
          <Route path="/records/:id" element={<RecordDetail />} />
          <Route path="/search" element={<SearchUploads />} />
          <Route path="/institutions/:slug" element={<InstitutionLanding />} />
          <Route path="/resource" element={<Resource />} />
          <Route path="/resource-dissertation" element={<ResourceDissertation />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/membership-terms" element={<MembershipTerms />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/signup/*" element={<Signup />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/docs" element={<Support />} />
          <Route path="/docs/:slug" element={<DocsPage />} />
          <Route path="/support" element={<Navigate to="/docs" replace />} />
          <Route path="/support/:slug" element={<DocsRedirect />} />
          <Route path="/support01b" element={<Navigate to="/docs" replace />} />
          {/* Hidden, link-only route: not referenced in navigation */}
          <Route path="/university-savings" element={<Calculator />} />
        </Routes></Suspense>
      </main>

      {/* Footer */}
      {!hideNavFooter && <Footer />}
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <ServerCacheProvider><Router>
          <AppContent />
        </Router></ServerCacheProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
